export interface EventSelectionEntity {
  id: string;
  available: number;
  memberOnlyEvent: boolean;
}

export interface EventSelectionContext {
  eventIds: string[];
  membershipId: string | null;
}

/**
 * Determines if an event row is selectable in the checkout add event modal.
 *
 * An event is selectable when:
 * - It has available capacity (available > 0)
 * - It is not already in the current order
 * - If it's a member-only event, the order must be a membership order
 *
 * @param entity - The event entity to check
 * @param context - The selection context containing order information
 * @returns true if the event row can be selected, false otherwise
 *
 * @example
 * // Regular event with availability - selectable
 * isEventRowSelectable(
 *   { id: '1', available: 10, memberOnlyEvent: false },
 *   { eventIds: [], membershipId: null }
 * ) // returns true
 *
 * @example
 * // Member-only event without membership order - NOT selectable
 * isEventRowSelectable(
 *   { id: '1', available: 10, memberOnlyEvent: true },
 *   { eventIds: [], membershipId: null }
 * ) // returns false
 *
 * @example
 * // Member-only event with membership order - selectable
 * isEventRowSelectable(
 *   { id: '1', available: 10, memberOnlyEvent: true },
 *   { eventIds: [], membershipId: 'membership-123' }
 * ) // returns true
 */
export function isEventRowSelectable(
  entity: EventSelectionEntity,
  context: EventSelectionContext
): boolean {
  const eventIds = Array.isArray(context.eventIds) ? context.eventIds : [];
  const isAlreadyInOrder = eventIds.includes(entity.id);

  const isMembershipOrder = context.membershipId !== null;
  const isMemberOnlyEvent = !!entity.memberOnlyEvent;

  // Disable if: no availability, already in order, or member-only event without membership order
  return entity.available > 0 && !isAlreadyInOrder && (!isMemberOnlyEvent || isMembershipOrder);
}

